import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight, Search, MoreHorizontal, Settings, X, ChevronsLeft, ChevronsRight, ChevronLeft } from 'lucide-react';
import { useGetSubCategoryList, useAddSubCategory, useUpdateSubCategoryStatus, useUpdateSubCategory, useDeleteSubCategory } from "../../../hooks/usePromptGroup"
import { useListLanguages } from "../../../hooks/useLanguages"
import { selectedLanguageStore, setSelectedLanguage } from '../../../stores/selectedLanguage';
import { useStore } from '@tanstack/react-store';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Modal from '../../common/ModalComponent';


const Prompts = () => {
    const { selectedLanguage } = useStore(selectedLanguageStore)
    const [languages, setLanguages] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [title, setTitle] = useState("")
    const { group_id } = useParams()
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(10);
    const [showAddPromptModal, setShowAddPromptModal] = useState(false);
    const [showEditPromptModal, setShowEditPromptModal] = useState(false);
    const [showDeletePromptModal, setShowDeletePromptModal] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [editingPrompt, setEditingPrompt] = useState(null);
    const [deletingPrompt, setDeletingPrompt] = useState(null);
    const { data: subCategoriesResponse, isSuccess: isSuccessSubCategories } = useGetSubCategoryList(`?category_id=${group_id}&language=${selectedLanguage}&search_key=${searchTerm}`)
    const { data: languagesListResponse, isSuccess: isSuccessLanguagesList } = useListLanguages('')
    const addMutation = useAddSubCategory();
    const updateStatusMutation = useUpdateSubCategoryStatus();
    const updateMutation = useUpdateSubCategory();
    const deleteMutation = useDeleteSubCategory();
    const queryClient = useQueryClient();
    const dropdownRef = useRef(null);

    const handleAddPrompt = () => {
        setTitle('');
        setShowAddPromptModal(true);
    };

    const handleEditPrompt = (prompt) => {
        setTitle(prompt?.title);
        setEditingPrompt(prompt)
        setShowEditPromptModal(true);
    };

    const handleDeletePrompt = (prompt) => {
        setDeletingPrompt(prompt)
        setShowDeletePromptModal(true);
    };

    const confirmDelete = () => {
        if (deletingPrompt) {
            deleteMutation.mutate(deletingPrompt?._id, {
                onSuccess: () => {
                    toast.success("Prompt has been deleted.")
                    queryClient.invalidateQueries({ queryKey: ['prompt_sub_category'] });
                    setShowDeletePromptModal(false);
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            })
        }
    };

    const resetStates = () => {
        setTitle("")
    }

    const handleFormSubmit = (type) => {
        if (title == "") {
            toast.error("Prompt title is not allowed to be empty");
            return false
        }
        const dataObj = {
            title,
            category_id: group_id,
            language: selectedLanguage
        }
        if (type === "edit") {
            dataObj._id = editingPrompt?._id
        }
        if (type === 'add') {
            addMutation.mutate(dataObj, {
                onSuccess: () => {
                    toast.success("New Prompt has been added.")
                    queryClient.invalidateQueries({ queryKey: ['prompt_sub_category'] });
                    setShowAddPromptModal(false);
                    resetStates()
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            });
        } else if (type === 'edit') {
            updateMutation.mutate(dataObj, {
                onSuccess: () => {
                    toast.success("Prompt has been updated.")
                    queryClient.invalidateQueries({ queryKey: ['prompt_sub_category'] });
                    setShowEditPromptModal(false);
                    resetStates()
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            });
        }
    };

    const toggleStatus = (id, currentStatus) => {
        updateStatusMutation.mutate({
            status: currentStatus == 1 ? 3 : 1,
            _id: id
        }, {
            onSuccess: () => {
                toast.success("Prompt status updated.")
                queryClient.invalidateQueries({ queryKey: ['prompt_sub_category'] });
            },
            onError: (error) => {
                const message = error.response?.data?.message
                toast.error(message)
            }
        })
    };

    const toggleDropdown = (itemId) => {
        setOpenDropdown(openDropdown === itemId ? null : itemId);
    };

    const handleDropdownAction = (action, item) => {
        if (action === "delete") {
            handleDeletePrompt(item)
        } else if (action === "edit") {
            handleEditPrompt(item)
        }
        setOpenDropdown(null);
    };

    const renderBreadcrumb = () => {
        const breadcrumbItems = [
            { label: 'Dashboard', path: 'dashboard' },
            { label: 'Profile Metadata', path: 'profile-metadata' },
            { label: 'Prompt Group', path: 'prompt-groups' },
            { label: 'Prompts', path: 'prompts' }
        ];
        return (
            <div className="flex items-center space-x-2 text-sm text-gray-500">
                {breadcrumbItems.map((item, index) => (
                    <React.Fragment key={item.path}>
                        <span className={index === breadcrumbItems.length - 1 ? 'text-gray-900' : 'text-gray-500'}>
                            {item.label}
                        </span>
                        {index < breadcrumbItems.length - 1 && <ChevronRight className="w-4 h-4" />}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (isSuccessLanguagesList) {
            setLanguages(languagesListResponse?.data?.data?.result)
        }
    }, [languagesListResponse, isSuccessLanguagesList])

    useEffect(() => {
        if (isSuccessSubCategories) {
            setSubCategories(subCategoriesResponse?.data?.data?.result ?? [])
            setTotalPages(subCategoriesResponse?.data?.data?.total_pages)
        }
    }, [isSuccessSubCategories, subCategoriesResponse])

    return (
        <div className="flex min-h-screen bg-gray-50">
            <div className="flex-1 flex flex-col">
                <div className="bg-white border-b border-gray-200 px-8 py-4">
                    {renderBreadcrumb()}
                </div>
                <div className="flex-1 p-8">
                    <div className="max-w-7xl">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">Prompts</h1>
                                <p className="text-gray-600">
                                    Add frequently answered prompts for users to see on your app and the website.
                                </p>
                            </div>
                            <button
                                onClick={handleAddPrompt}
                                className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors font-medium">
                                Add New Prompt
                            </button>
                        </div>
                        {/* Language Selector */}
                        <div className="mb-6 flex space-x-2">
                            {languages.map((language) => (
                                <button
                                    key={language}
                                    className={`px-4 py-2 rounded ${selectedLanguage === language?.short_code ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-700'}`}
                                    onClick={() => setSelectedLanguage(language?.short_code)}>
                                    {language?.name}
                                </button>
                            ))}
                        </div>
                        {/* Search */}
                        <div className="mb-6">
                            <div className="relative max-w-md">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder={'Search prompt'}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        {/* Table */}
                        <div className="bg-white rounded-lg border border-gray-200">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prompt</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {subCategories?.map((prompt, index) => (
                                        <tr key={prompt?._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">{index + 1} </td>
                                            <td
                                                className="px-6 py-4 text-sm font-medium text-gray-900">
                                                {prompt?.title}
                                            </td>
                                            <td className="px-8 py-4 text-sm text-gray-900">{prompt?.prompt_category_data?.title}</td>
                                            <td className="px-4 py-4 text-center">
                                                <button
                                                    onClick={() => toggleStatus(prompt?._id, prompt?.status)}
                                                    className="focus:outline-none">
                                                    <div className={`w-12 h-6 rounded-full flex items-center ${prompt?.status == 1 ? 'bg-gray-900' : 'bg-gray-300'} p-1 transition-colors duration-200`}>
                                                        <div className={`w-4 h-4 rounded-full bg-white transform transition-transform duration-200 ${prompt.status == 1 ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                                    </div>
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-400">
                                                <div className="relative" ref={openDropdown === prompt?._id ? dropdownRef : null}>
                                                    <button
                                                        onClick={() => toggleDropdown(prompt?._id)}
                                                        className="hover:text-gray-600 p-1 rounded hover:bg-gray-100">
                                                        <MoreHorizontal className="w-5 h-5" />
                                                    </button>
                                                    {openDropdown === prompt?._id && (
                                                        <div className="absolute right-0 top-8 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                                                            <div className="py-1">
                                                                <button
                                                                    onClick={() => handleDropdownAction('edit', prompt)}
                                                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2">
                                                                    <Settings className="w-4 h-4" />
                                                                    <span>Edit</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDropdownAction('delete', prompt)}
                                                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2">
                                                                    <X className="w-4 h-4" />
                                                                    <span>Delete</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {/* Pagination */}
                            <div className="flex justify-end mb-3 mr-3" >
                                <div className="flex items-center">
                                    <div className="mr-4 flex justify-between">
                                        <span className="mt-1 mr-2 text-sm text-gray-500">Rows per page</span>
                                        <select
                                            className="w-[55px] border border-gray-300 rounded p-1 text-sm"
                                            value={rowsPerPage}
                                            onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </select>
                                    </div>
                                    <div className="flex items-center text-sm">
                                        <span className="mr-4">Page {currentPage} of {totalPages}</span>
                                        <div className="flex">
                                            <button
                                                className="p-1 border border-gray-300 rounded-l hover:bg-gray-100"
                                                onClick={() => setCurrentPage(1)}
                                                disabled={currentPage === 1}>
                                                <ChevronsLeft size={16} />
                                            </button>
                                            <button
                                                className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}>
                                                <ChevronLeft size={16} />
                                            </button>
                                            <button
                                                className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                disabled={currentPage === totalPages}>
                                                <ChevronRight size={16} />
                                            </button>
                                            <button
                                                className="p-1 border border-gray-300 rounded-r hover:bg-gray-100"
                                                onClick={() => setCurrentPage(totalPages)}
                                                disabled={currentPage === totalPages}>
                                                <ChevronsRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Add / Edit Prompts Modal */}
            {showAddPromptModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">Add New Prompt</h2>
                                    <p className="text-sm text-gray-500 mt-1">Add a new prompt inside your selected group</p>
                                </div>
                                <button
                                    onClick={() => setShowAddPromptModal(false)}
                                    className="text-gray-400 hover:text-gray-600">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Prompt Title*
                                    </label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Enter"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                                <button
                                    onClick={() => setShowAddPromptModal(false)}
                                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleFormSubmit('add')}
                                    className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors">
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showEditPromptModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">Edit Prompt</h2>
                                    <p className="text-sm text-gray-500 mt-1">Manage an existing prompt</p>
                                </div>
                                <button
                                    onClick={() => setShowEditPromptModal(false)}
                                    className="text-gray-400 hover:text-gray-600">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Prompt title*
                                    </label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Enter"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                                <button
                                    onClick={() => setShowEditPromptModal(false)}
                                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleFormSubmit('edit')}
                                    className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors">
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Modal
                title={<h2 className="text-lg font-semibold text-gray-900">Delete Prompt</h2>}
                isOpen={showDeletePromptModal}
                onClose={() => setShowDeletePromptModal(false)}
                children={
                    <div className="bg-white rounded-lg w-full">
                        <p className="text-sm text-gray-600 mb-6">
                            Are you sure you want to delete this Prompt? This action can't be undone
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeletePromptModal(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors">
                                Delete Now
                            </button>
                        </div>
                    </div>
                }
            />
        </div>
    );
};
export default Prompts;